import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mantine/core';
import turfBbox from '@turf/bbox';
import center from '@turf/center';
import { featureCollection } from '@turf/helpers';
import mapboxgl, { GeoJSONSourceRaw, LngLatBoundsLike } from 'mapbox-gl';

import { COLORS } from 'constants/colors';
import { RISK_AREA_TYPES } from 'constants/fieldRisks';
import { FIELD_OUTLINE, MAPBOX_FIT_PARAMS } from 'constants/mapbox';
import { NUTRIENT_PANEL, PRESSURE_PANEL } from 'constants/products';
import { BCSR, MAP_ANALYTIC, mapDisplayTypes, SOIL_ATTRIBUTES } from 'constants/results';

import { getHighestRiskFromOrder } from 'util/chartUtils';
import { clipFeaturesToBoundary } from 'util/geospatial';
import useBroswerLanguage from 'util/hooks/useLanguage';
import { getLegendMarks, getLegendTitle, getMapLegendColorStops } from 'util/mapImageryColors';
import { getFieldResultsConfig } from 'util/overviewResultsDisplay';
import {
  getNonTillageProMapPaintFill,
  getProLayerHighLow,
  setNonTillageProHoverHandlers,
} from 'util/proMaps';
import { handleJsonResponse } from 'util/request';
import {
  adjustSamplesForInterpolation,
  getDisplaySamplesForAnalytic,
  getMidwestBenchmarkText,
  getMinMaxSampleRange,
} from 'util/results';
import { getProNutrientMapLayer } from 'util/samplePlan';
import { getString } from 'strings/translation';
import { AnalyticType, SingleAnalyticType } from 'store/analytics/types';
import { FieldType, MapboxSample, SamplingPlanType } from 'store/fields/types';
import { RecommendationType } from 'store/recommendations/types';
import useMapboxGl from 'common/MapHooks';
import Popup from 'common/Maps/Popup';
import { PopupState, ViewPortProps } from 'common/Maps/types';

import { MapDisplayTypeSelector } from '../common/MapDisplayTypeSelector';
import { LegendWrap } from '../common/MapLegend/LegendWrap';
import createAnalyticPopup, {
  getProLayerPopupContent,
  getProNutrientPopupSettings,
} from '../common/MapPopup';

import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './Map.module.css';

interface MapProps {
  activeAnalytic: AnalyticType;
  field: FieldType;
  samples: MapboxSample[];
  samplingPlan: SamplingPlanType;
  midwestBenchmark: SingleAnalyticType;
  recommendations: RecommendationType[];
}

type MapDisplayType = keyof typeof mapDisplayTypes;

const Map = ({
  field,
  samplingPlan,
  activeAnalytic,
  samples,
  midwestBenchmark,
  recommendations,
}: MapProps) => {
  const language = useBroswerLanguage();
  const [centerLongitude, centerLatitude] = center(field).geometry?.coordinates as number[];

  const proLayer = getProNutrientMapLayer(samplingPlan, activeAnalytic);

  const [mapDisplayType, setMapDisplayType] = useState<MapDisplayType>(proLayer ? 'pro' : 'points');
  const mapPointsId = `${MAP_ANALYTIC}-${samplingPlan.field_id}-${samplingPlan.id}-${activeAnalytic.id}-points`;
  const mapProId = `${MAP_ANALYTIC}-${samplingPlan.field_id}-${samplingPlan.id}-${activeAnalytic.id}-pro`;
  const pointsQuantityId = `${mapPointsId}-quantity`;

  const [initialViewport, setInitialViewport] = useState({
    latitude: centerLatitude,
    longitude: centerLongitude,
    zoom: 5.5,
  });

  const [viewport, setViewport] = useState<ViewPortProps>({
    latitude: centerLatitude,
    longitude: centerLongitude,
    zoom: 5.5,
    width: 0,
    height: 0,
  });

  const [popupInfo, setPopupInfo] = useState<PopupState>(null);
  const [mapHasLoaded, setMapHasLoaded] = useState(false);
  const [samplingPlanId, setSamplingPlanId] = useState<number | null>(null);
  const [analyticId, setAnalyticId] = useState(activeAnalytic.id);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef(null);

  const [trimmedFc, setTrimmedFc] = useState<ReturnType<typeof featureCollection> | null>();

  const trimLayer = async () => {
    if (proLayer) {
      try {
        const response = await handleJsonResponse(await fetch(proLayer.geojson_uri));
        const clipped = clipFeaturesToBoundary(response.features, field.features[0]);

        setTrimmedFc(clipped);
      } catch (error) {
        // eslint-disable-next-line
        console.error('Unable to trim geojson fs');
        // eslint-disable-next-line
        console.error(error);
      }
    }
  };

  useEffect(() => {
    trimLayer();
  }, [proLayer]);

  useMapboxGl(mapContainerRef, mapRef, null, viewport, setViewport, () => {}, true, null, null);

  const midwestBenchmarkRiskSummary = useMemo(
    () => getMidwestBenchmarkText(midwestBenchmark),
    [midwestBenchmark],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      map.on('load', () => {
        setMapHasLoaded(true);
      });
    }
  }, [mapRef, setMapHasLoaded]);

  const { planAnalytic } = getFieldResultsConfig(
    activeAnalytic,
    samplingPlan,
    samples,
    recommendations,
    field,
  );

  const resetMapLayers = () => {
    const map = mapRef.current;
    map?.getStyle().layers;
    if (mapHasLoaded && map) {
      [mapPointsId, mapProId, pointsQuantityId].forEach((layer) => {
        if (map.getLayer(layer)) {
          map.removeLayer(layer);
          map.removeSource(layer);
        }
      });
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (mapHasLoaded && map) {
      if (mapDisplayType === 'points') {
        setAnalyticId(activeAnalytic.id);
        const displaySamples = getDisplaySamplesForAnalytic(samples, activeAnalytic);
        const analyticSamples = samplingPlan.is_pro
          ? displaySamples.filter((sample) => {
              const panelToSearch = [BCSR, SOIL_ATTRIBUTES].includes(activeAnalytic.category)
                ? NUTRIENT_PANEL
                : PRESSURE_PANEL;
              return sample.properties.products.includes(panelToSearch);
            })
          : displaySamples;
        const newFeatureCollection = featureCollection(analyticSamples);
        const source = {
          type: 'geojson',
          data: newFeatureCollection,
        } as GeoJSONSourceRaw;
        const analysisAdjustedSamples = adjustSamplesForInterpolation(
          // @ts-ignore
          analyticSamples,
          activeAnalytic,
          samplingPlan,
        );
        const adjustedSource = {
          type: 'geojson',
          data: featureCollection(analysisAdjustedSamples),
        };
        const mapId = `${MAP_ANALYTIC}-${samplingPlan.field_id}-${samplingPlan.id}-${activeAnalytic.id}`;
        const removeMapId = `${MAP_ANALYTIC}-${samplingPlan.field_id}-${samplingPlanId}-${activeAnalytic.id}`;
        if (map.getLayer(removeMapId)) {
          map.removeLayer(removeMapId);
          map.removeSource(removeMapId);
          map.removeLayer(`${removeMapId}-quantity`);
          map.removeSource(`${removeMapId}-quantity`);
        }
        if (samples.length) {
          map.addLayer({
            id: mapId,
            type: 'fill',
            source,
            paint: {
              'fill-color': ['get', 'fill-color'],
              'fill-outline-color': COLORS.white,
            },
          });
          map.addLayer({
            id: `${mapId}-quantity`,
            type: 'symbol',
            // @ts-ignore
            source: adjustedSource,
            layout: {
              'text-field': ['get', 'quantity'],
              'text-allow-overlap': true,
              'text-justify': 'center',
              'text-size': 10,
            },
          });
        }
        map.on('click', mapId, (e) => {
          if (e.features?.[0]?.properties && mapRef.current && !map.getLayer(mapProId)) {
            const { id } = e.features[0].properties;
            const foundValidSample = samples.find((val) => val.properties.id === id);

            if (foundValidSample) {
              const content = createAnalyticPopup(
                foundValidSample,
                activeAnalytic,
                midwestBenchmarkRiskSummary,
                language,
              );

              setPopupInfo({ ...e.lngLat, content });
            }
          }
        });
      } else if (proLayer && planAnalytic?.data_summary) {
        if (!map.getLayer(mapProId)) {
          const source = {
            type: 'geojson',
            data: trimmedFc || proLayer.geojson_uri,
          } as GeoJSONSourceRaw;

          map.addSource(mapProId, source);

          const sampleRange = getMinMaxSampleRange(samples, activeAnalytic);

          const proHighLow = getProLayerHighLow(
            mapDisplayType === 'highContrast',
            activeAnalytic.id,
            sampleRange,
            undefined, // TODO: change this for https://earthoptics.atlassian.net/browse/SE-48
            planAnalytic?.data_summary,
            proLayer,
          );

          map.addLayer({
            id: mapProId,
            type: 'fill',
            source: mapProId,
            paint: {
              'fill-color': getNonTillageProMapPaintFill(
                activeAnalytic,
                planAnalytic.data_summary,
                proLayer,
                proHighLow,
              ),
              'fill-opacity': 1,
            },
          });

          map.on('click', mapProId, (evt) => {
            const { labelPrefix, layerParam, units } = getProNutrientPopupSettings(
              activeAnalytic.id,
              proLayer,
              planAnalytic.unit,
            );

            const content = getProLayerPopupContent(
              map,
              evt,
              layerParam,
              mapProId,
              labelPrefix,
              units,
            );

            setPopupInfo(content ? { ...evt.lngLat, content } : null);
          });

          setNonTillageProHoverHandlers(map, mapProId);
        }
      }

      map.getStyle();
      setSamplingPlanId(samplingPlan.id);
    }
  }, [
    mapHasLoaded,
    samples,
    analyticId,
    activeAnalytic,
    samplingPlan,
    mapRef,
    midwestBenchmarkRiskSummary,
    samplingPlanId,
    language,
    mapDisplayType,
    planAnalytic,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (mapHasLoaded && map) {
      const bbox = turfBbox(field) as LngLatBoundsLike;

      const source = { type: 'geojson', data: field } as GeoJSONSourceRaw;
      if (map.getLayer(FIELD_OUTLINE)) {
        map.removeLayer(FIELD_OUTLINE);
      }
      if (map.getSource(FIELD_OUTLINE)) {
        map.removeSource(FIELD_OUTLINE);
      }

      map.addLayer({
        id: FIELD_OUTLINE,
        type: 'line',
        source,
        paint: { 'line-color': COLORS.white, 'line-width': 2 },
      });

      map.fitBounds(bbox, MAPBOX_FIT_PARAMS);
      const zoom = map.getZoom();
      setViewport((prevViewport) => ({
        ...prevViewport,
        centerLatitude,
        centerLongitude,
        zoom,
      }));
      setInitialViewport((prevViewport) => ({
        ...prevViewport,
        latitude: centerLatitude,
        longitude: centerLongitude,
        zoom,
      }));
    }
  }, [centerLatitude, centerLongitude, field, mapHasLoaded, mapRef]);

  const recenterMap = () => {
    setViewport(initialViewport);
    if (mapRef.current) {
      mapRef.current.setZoom(initialViewport.zoom);
      mapRef.current.setCenter([initialViewport.longitude, initialViewport.latitude]);
    }
  };

  const changeMapDisplayType = (element: string) => {
    resetMapLayers();
    setMapDisplayType(element as MapDisplayType);
    setPopupInfo(null);
  };

  const proHighLow = getProLayerHighLow(
    mapDisplayType === 'highContrast',
    activeAnalytic.id,
    undefined,
    undefined,
    undefined,
    proLayer,
  );

  const riskLevelOrder =
    planAnalytic?.risk_summary?.risk_level_order || RISK_AREA_TYPES.LOW_MODERATE_HIGH;

  const highestRiskOrder = getHighestRiskFromOrder(riskLevelOrder);
  const legendMarks = proHighLow ? getLegendMarks(proHighLow.high, proHighLow.low) : [];

  return (
    <Box className={styles.Wrapper}>
      <Box className={styles.MapWrapper}>
        <Box ref={mapContainerRef} className={styles.MapWrapper}>
          {mapDisplayType !== 'points' && (
            <LegendWrap
              showPlus
              title={getLegendTitle(activeAnalytic.id, false, false, planAnalytic?.unit, proLayer)}
              marks={legendMarks}
              rootBoxProps={{
                top: 60, // below layer controls
              }}
              colorStops={getMapLegendColorStops(
                getHighestRiskFromOrder(highestRiskOrder) || '',
                activeAnalytic.id,
              )}
            />
          )}
          {popupInfo && mapRef.current && (
            <Popup
              {...popupInfo}
              map={mapRef.current}
              anchor="bottom"
              onClose={() => setPopupInfo(null)}
            >
              {popupInfo.content}
            </Popup>
          )}
        </Box>
        <Button variant="white" className={styles.Recenter} onClick={recenterMap}>
          {getString('recenter', language)}
        </Button>
        {!!proLayer && (
          <MapDisplayTypeSelector
            className={styles.Toggle}
            language={language}
            onChange={changeMapDisplayType}
            value={mapDisplayType}
          />
        )}
      </Box>
    </Box>
  );
};

export default Map;
